/** Every message crossing the service, both faces. Reading a body is audited. */

import { memo, useCallback, useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'
import {
  Button,
  Caption1,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  Chip,
  CodeBlock,
  DataTableShell,
  EmptyState,
  FilterBar,
  IDChip,
  KeyValueList,
  KeyValueRow,
  LoadingState,
  MonoText,
  Pill,
  StatusBadge,
  formatTimestamp,
  useDetailDrawer,
} from '../platform'
import { api } from '../api'
import { toQuery, type MessageFilters } from './communicationFilters'
import type { MessageSummary, Page } from '../api'
import { useAuth } from '../auth'

const DIRECTIONS = ['inbound', 'outbound'] as const

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 719px)': {
      width: 'calc(100vw - 80px)',
    },
  },
  searchForm: {
    width: '340px',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 719px)': {
      width: '100%',
    },
  },
  search: {
    width: '100%',
    minWidth: 0,
  },
  searchFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  table: {
    width: '860px',
    minWidth: '820px',
    tableLayout: 'fixed',
  },
  row: {
    cursor: 'pointer',
    '@media (max-width: 719px)': {
      height: '44px',
    },
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  timeColumn: {
    width: '190px',
  },
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  drawerAction: {
    alignSelf: 'flex-start',
  },
  note: {
    color: tokens.colorNeutralForeground3,
  },
})

export default function Communications() {
  const styles = useStyles()
  const drawer = useDetailDrawer<MessageSummary>()
  const [rows, setRows] = useState<MessageSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [direction, setDirection] = useState<string | null>(null)
  const [correlationId, setCorrelationId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const filters: MessageFilters = {
    direction,
    correlationId: correlationId || undefined,
    from: from || undefined,
    to: to || undefined,
  }

  // Asked of the service, not of the rows already on screen. The page used to
  // narrow what it held, which answers "are there outbound messages among these
  // fifty" over a question that was "are there outbound messages".
  const load = useCallback((active: MessageFilters, after?: string | null) => {
    if (after) setLoadingMore(true)
    api
      .get<Page<MessageSummary>>(`/communications${toQuery(active, after)}`)
      .then((page) => {
        setRows((current) => (after ? [...(current ?? []), ...page.items] : page.items))
        setCursor(page.next_cursor ?? null)
        setError(null)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMore(false))
  }, [])

  useEffect(() => {
    // No cursor: a new question starts at its first page. Carrying one over
    // would answer the new filter from wherever the old list happened to stop.
    load(filters, null)
  }, [load, direction, from, to]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <EmptyState title="The log cannot be read" description={error} />

  const shown = rows ?? []

  return (
    <div className={styles.page}>
      <FilterBar
        search={
          <div className={styles.searchGroup}>
            <form
              className={styles.searchForm}
              onSubmit={(e) => {
                e.preventDefault()
                load(filters, null)
              }}
            >
              <Input
                className={styles.search}
                value={correlationId}
                onChange={(_, data) => setCorrelationId(data.value)}
                placeholder="Correlation id. One authorization, both directions."
                aria-label="Find one authorization"
              />
            </form>
            <div className={styles.searchFilters}>
              {DIRECTIONS.map((value) => (
                <Chip
                  key={value}
                  selected={direction === value}
                  onClick={() => setDirection(direction === value ? null : value)}
                >
                  {value}
                </Chip>
              ))}
            </div>
            <div className={styles.searchFilters}>
              <Input
                type="datetime-local"
                value={from}
                onChange={(_, data) => setFrom(data.value)}
                aria-label="From"
              />
              <Input
                type="datetime-local"
                value={to}
                onChange={(_, data) => setTo(data.value)}
                aria-label="To"
              />
            </div>
          </div>
        }
      />

      <DataTableShell
        title="Communications"
        description="Identifiers were replaced by their blind index before any of this was stored, so a body here holds no card number. Reading one is still recorded, because it is still someone's charging behaviour."
        footerStart={`${shown.length} ${shown.length === 1 ? 'message' : 'messages'} loaded`}
      >
        {!rows ? (
          <LoadingState label="Reading the communications log" />
        ) : shown.length === 0 ? (
          <EmptyState
            title="No messages to show"
            description="Nothing has crossed the service in this window."
          />
        ) : (
          <Table size="small" className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.timeColumn}>Time, Berlin</TableHeaderCell>
                <TableHeaderCell>Direction</TableHeaderCell>
                <TableHeaderCell>Peer</TableHeaderCell>
                <TableHeaderCell>Operation</TableHeaderCell>
                <TableHeaderCell>Outcome</TableHeaderCell>
                <TableHeaderCell>Latency</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((message) => (
                <MessageRow
                  key={message.message_id}
                  message={message}
                  onOpen={drawer.open}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {/* The end of the list says which end it is. Without this a reader
            cannot tell a page that holds everything from one that stopped at
            fifty, and the second is what this page did for every question. */}
        {shown.length > 0 ? (
          <div className={styles.searchFilters}>
            {cursor ? (
              <Button onClick={() => load(filters, cursor)} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : `Load more, ${shown.length} so far`}
              </Button>
            ) : (
              <Caption1>{`All ${shown.length} that match are shown.`}</Caption1>
            )}
          </div>
        ) : null}
      </DataTableShell>
    </div>
  )
}

export const MessageRow = memo(function MessageRow({
  message,
  onOpen,
}: {
  message: MessageSummary
  onOpen: (message: MessageSummary) => void
}) {
  const styles = useStyles()
  return (
    <TableRow
      className={styles.row}
      tabIndex={0}
      aria-label={`Open communication ${message.operation}`}
      onClick={() => onOpen(message)}
      onKeyDown={(event) => openRowFromKeyboard(event, () => onOpen(message))}
    >
      <TableCell className={mergeClasses(styles.nowrap, styles.timeColumn)}>
        <MonoText>{formatTimestamp(message.occurred_at)}</MonoText>
      </TableCell>
      <TableCell>
        <Pill>{message.direction}</Pill>
      </TableCell>
      <TableCell>{message.peer}</TableCell>
      <TableCell>
        <MonoText>{message.operation}</MonoText>
      </TableCell>
      <TableCell>
        {message.outcome ? (
          <StatusBadge
            status={message.outcome.startsWith('2') ? 'success' : 'warning'}
            label={message.outcome}
          />
        ) : (
          <Caption1 className={styles.note}>none recorded</Caption1>
        )}
      </TableCell>
      <TableCell className={styles.nowrap}>
        <MonoText>
          {message.latency_ms === null ? 'not timed' : `${message.latency_ms} ms`}
        </MonoText>
      </TableCell>
    </TableRow>
  )
})

function openRowFromKeyboard(event: KeyboardEvent, open: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  open()
}

/**
 * The body is fetched only when someone asks for it, never on opening the row.
 * The audit entry is written by the service on that read, so an entry always
 * means a person chose to look.
 */
function MessageDrawerBody({ message }: { message: MessageSummary }) {
  const styles = useStyles()
  const { principal } = useAuth()
  const [payload, setPayload] = useState<string | null>(null)
  const [asked, setAsked] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const mayRead = principal?.role === 'operator' || principal?.role === 'admin'

  const read = () => {
    setAsked(true)
    api
      .get<{ message_id: string; payload: string | null }>(
        `/communications/${message.message_id}/payload`,
      )
      .then((body) => setPayload(body.payload))
      .catch((e) => setFailed(e.message))
  }

  return (
    <div className={styles.drawer}>
      <KeyValueList divided>
        <KeyValueRow label="Time, Berlin" value={formatTimestamp(message.occurred_at)} />
        <KeyValueRow label="Direction" value={message.direction} />
        <KeyValueRow label="Peer" value={message.peer} />
        <KeyValueRow label="Operation" value={<MonoText>{message.operation}</MonoText>} />
        <KeyValueRow label="Outcome" value={message.outcome ?? 'none recorded'} />
        <KeyValueRow
          label="Latency"
          value={message.latency_ms === null ? 'not timed' : `${message.latency_ms} ms`}
        />
        <KeyValueRow
          label="Correlation"
          value={
            message.correlation_id ? (
              <IDChip value={message.correlation_id} tone="brand" copyable />
            ) : (
              'none, nothing was in flight'
            )
          }
        />
      </KeyValueList>

      {asked ? (
        failed ? (
          <Caption1 className={styles.note}>{failed}</Caption1>
        ) : (
          <CodeBlock wrap maxHeight={360} copyValue={payload ?? ''}>
            {payload ?? 'Stored as metadata only.'}
          </CodeBlock>
        )
      ) : (
        <Button className={styles.drawerAction} onClick={read} disabled={!mayRead}>
          {mayRead ? 'Read the body, this is recorded' : 'Operators and admins only'}
        </Button>
      )}
    </div>
  )
}

export const communicationsDrawer = {
  variant: 'overlay' as const,
  renderTitle: (message: MessageSummary) => message.operation,
  renderSubtitle: (message: MessageSummary) => (
    <MonoText tone="muted">
      {message.direction}, {message.peer}
    </MonoText>
  ),
  render: (message: MessageSummary) => <MessageDrawerBody message={message} />,
}
